import React, { useState } from 'react';
import { 
  X, User, GraduationCap, Stethoscope, Sparkles, CheckCircle2, 
  BarChart2, Printer, ShieldCheck, Activity, Award, AlertCircle
} from 'lucide-react';
import { DemoStudent } from '../data/demoStudents';

const ALL_30_MODULE_META = [
  { key: 'aac', label: '01. AAC Speech Synthesizer', category: 'Assistive Communication' },
  { key: 'speech-therapy', label: '02. Speech & Articulation', category: 'Clinical Therapy' },
  { key: 'autism', label: '03. Autism & ABA Behavior', category: 'Behavioral Health' },
  { key: 'sign-language', label: '04. Sign Language & Gestures', category: 'Visual / Hearing' },
  { key: 'braille', label: '05. Braille & Tactile Learning', category: 'Visual Impairment' },
  { key: 'student', label: '06. Student & IEP Progress', category: 'Special Education' },
  { key: 'therapist', label: '07. Therapist Workspace & SOAP', category: 'Clinical Operations' },
  { key: 'teacher', label: '08. Teacher & Classroom Lesson', category: 'School Education' },
  { key: 'assessment', label: '09. Diagnostic Assessment', category: 'Evaluations' },
  { key: 'school-erp', label: '10. Special School ERP', category: 'Administration' },
  { key: 'hospital', label: '11. Rehabilitation Hospital', category: 'Inpatient Care' },
  { key: 'billing', label: '12. NDIS & Insurance Billing', category: 'Finance' },
  { key: 'communication', label: '13. Parent Communication', category: 'Family Engagement' },
  { key: 'inventory', label: '14. Assistive Device Inventory', category: 'Resources' },
  { key: 'analytics', label: '15. Executive Analytics BI', category: 'Reporting' },
  { key: 'ai', label: '16. AI Copilot Summarizer', category: 'Intelligence' },
  { key: 'sensory-room', label: '17. Sensory Room Controller', category: 'Environmental' },
  { key: 'tele-rehab', label: '18. Tele-Rehabilitation Video', category: 'Tele-Health' },
  { key: 'early-intervention', label: '19. Early Intervention Milestone', category: 'Pediatrics' },
  { key: 'vocational-living', label: '20. Vocational & Life Skills', category: 'Adult Rehab' },
  { key: 'hydrotherapy', label: '21. Hydrotherapy Aquatic Rehab', category: 'Physical Rehab' },
  { key: 'music-therapy', label: '22. Music Auditory Stimulation', category: 'Neuro-Rehab' },
  { key: 'animal-therapy', label: '23. Animal-Assisted Therapy', category: 'Emotional' },
  { key: 'prosthetics', label: '24. Bionic Prosthetics & Fitting', category: 'Bionics' },
  { key: 'vbmapp-assessment', label: '25. VB-MAPP Assessment Protocol', category: 'Behavioral Health' },
  { key: 'core-vocab-ai', label: '26. Motor Core Vocab & AI', category: 'AAC AI' },
  { key: 'fim-weefim', label: '27. FIM Functional Scoring', category: 'Clinical Rehab' },
  { key: 'evv-gps-tracker', label: '28. EVV Visit Verification & GPS', category: 'Compliance' },
  { key: 'gamified-neuro', label: '29. WebGL Biofeedback Neuro-Rehab', category: 'Gamified Rehab' },
  { key: 'transition-matrix', label: '30. Secondary Transition Matrix', category: 'Post-Secondary' }
];

interface StudentClinicalAnalysisModalProps {
  student: DemoStudent | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectModule?: (moduleKey: string) => void;
}

export const StudentClinicalAnalysisModal: React.FC<StudentClinicalAnalysisModalProps> = ({
  student,
  isOpen,
  onClose,
  onSelectModule
}) => {
  const [exportedMsg, setExportedMsg] = useState('');

  if (!isOpen || !student) return null;

  const handleExportPDF = () => {
    setExportedMsg(`Clinical Analysis Report for ${student.name} exported as PDF!`);
    setTimeout(() => setExportedMsg(''), 4000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1c1b1f]/80 backdrop-blur-md animate-fade-in">
      <div className="bg-[#f9f6f0] rounded-[36px] shadow-2xl border border-black/10 w-full max-w-5xl max-h-[90vh] overflow-y-auto relative flex flex-col">
        
        {/* Clarvos Obsidian Modal Header */}
        <div className="bg-[#1c1b1f] p-6 sm:p-7 text-white sticky top-0 z-20 flex justify-between items-start border-b border-white/10">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-[#ffd64e]/10 text-[#ffd64e] border border-[#ffd64e]/30 text-[10px] font-black rounded-full uppercase tracking-wider">
                Individual Clinical Analysis & 30-Module Mastery
              </span>
              <span className="text-xs font-mono font-bold text-white/70">{student.rollId}</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">{student.name}</h2>
            <p className="text-xs text-white/70">
              Grade: <span className="text-white font-bold">{student.grade}</span> • Disability: <span className="text-[#ffd64e] font-black">{student.disabilityCategory}</span> • Therapist: <span className="text-white font-bold">{student.therapist}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPDF}
              className="px-5 py-2.5 bg-[#ffd64e] hover:bg-[#ffcf33] text-[#1c1b1f] font-black text-xs rounded-full shadow-md transition flex items-center gap-1.5"
            >
              <Printer className="w-4 h-4 text-[#1c1b1f]" />
              <span>Export PDF Report</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-white/70 hover:text-white rounded-full hover:bg-white/10 transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {exportedMsg && (
          <div className="p-3 bg-[#bfecc7]/20 border-b border-[#bfecc7]/40 text-[#1c1b1f] text-xs font-bold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-700" />
            {exportedMsg}
          </div>
        )}

        {/* Modal Body Content */}
        <div className="p-6 sm:p-8 space-y-6 flex-1">
          
          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            {/* Overall Mastery Gauge */}
            <div className="p-5 bg-white border border-black/10 rounded-[24px] flex items-center gap-4 shadow-sm">
              <div className="w-14 h-14 bg-[#1c1b1f] text-[#ffd64e] rounded-[18px] flex items-center justify-center font-black text-xl font-mono shadow-md shrink-0">
                {student.overallMasteryPercent}%
              </div>
              <div>
                <div className="text-xs font-black text-[#1c1b1f]">Overall IEP Mastery Index</div>
                <div className="text-[11px] text-[#1c1b1f]/60 font-medium">Calculated across 30 modules</div>
              </div>
            </div>

            {/* Active IEP Goal Target */}
            <div className="p-5 bg-white border border-black/10 rounded-[24px] space-y-1 shadow-sm">
              <div className="text-xs font-bold text-[#1c1b1f]/60 uppercase tracking-wider">Active SMART IEP Target</div>
              <div className="text-xs font-bold text-[#1c1b1f] line-clamp-2">{student.iepGoal}</div>
            </div>

            {/* Recent Assessment Score */}
            <div className="p-5 bg-white border border-black/10 rounded-[24px] space-y-1 shadow-sm">
              <div className="text-xs font-bold text-[#1c1b1f]/60 uppercase tracking-wider">Latest Assessment Score</div>
              <div className="text-sm font-black text-emerald-700 font-mono">{student.recentScore}</div>
            </div>

          </div>

          {/* AI Clinical Recommendations Box */}
          <div className="p-5 bg-[#ffd64e]/10 border border-[#ffd64e]/30 rounded-[24px] space-y-2">
            <div className="flex items-center gap-2 font-black text-[#1c1b1f] text-xs uppercase tracking-wider">
              <Sparkles className="w-4 h-4 text-[#d97706]" />
              AI Clinical Recommendation for {student.name}
            </div>
            <p className="text-xs text-[#1c1b1f]/80 font-medium leading-relaxed">
              {student.aiRecommendation}
            </p>
          </div>

          {/* Clinical Summary */}
          <div className="p-5 bg-white border border-black/10 rounded-[24px] space-y-1.5 text-xs shadow-sm">
            <div className="font-black text-[#1c1b1f]">Clinical Overview & Progress History:</div>
            <p className="text-[#1c1b1f]/70 leading-relaxed">{student.clinicalSummary}</p>
          </div>

          {/* ALL 30 MODULES CLINICAL MASTERY GRID */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-black/10 pb-3">
              <h3 className="text-base font-black text-[#1c1b1f] flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-[#1c1b1f]" />
                30-Module Mastery Breakdown for {student.name}
              </h3>
              <span className="text-xs font-bold text-[#1c1b1f]/60">Click any module to launch</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {ALL_30_MODULE_META.map((mod) => {
                const mastery = student.moduleMasteryMap[mod.key] || {
                  status: 'NOT_ASSESSED',
                  scorePercent: 0,
                  lastAssessedDate: 'N/A',
                  notes: 'Module not assessed yet'
                };

                return (
                  <div 
                    key={mod.key}
                    onClick={() => {
                      if (onSelectModule) {
                        onSelectModule(mod.key);
                        onClose();
                      }
                    }}
                    className="p-4 bg-white border border-black/10 rounded-[20px] shadow-sm hover:shadow-md hover:border-[#ffd64e] cursor-pointer transition-all space-y-2.5 group"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-black text-[#1c1b1f] group-hover:text-black transition truncate">
                        {mod.label}
                      </span>
                      
                      <span className={`px-2.5 py-0.5 rounded-full text-[9px] font-black shrink-0 ${
                        mastery.status === 'MASTERED' ? 'bg-[#bfecc7]/30 text-emerald-900 border border-[#bfecc7]' :
                        mastery.status === 'IN_PROGRESS' ? 'bg-[#ffd64e]/20 text-[#1c1b1f] border border-[#ffd64e]/40' :
                        mastery.status === 'NEEDS_ATTENTION' ? 'bg-amber-100 text-amber-900 border border-amber-300' :
                        'bg-[#eae7e1] text-[#1c1b1f]/60'
                      }`}>
                        {mastery.status.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Score Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold text-[#1c1b1f]/60">
                        <span>Mastery Score</span>
                        <span className="font-mono font-black text-[#1c1b1f]">{mastery.scorePercent}%</span>
                      </div>
                      <div className="w-full bg-[#eae7e1] h-2 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            mastery.scorePercent >= 85 ? 'bg-emerald-600' :
                            mastery.scorePercent >= 70 ? 'bg-[#ffd64e]' :
                            mastery.scorePercent > 0 ? 'bg-amber-500' : 'bg-black/20'
                          }`} 
                          style={{ width: `${mastery.scorePercent}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="text-[10px] text-[#1c1b1f]/60 truncate pt-1 border-t border-black/5">
                      <span className="font-bold text-[#1c1b1f]">Note:</span> {mastery.notes}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
